import { createElement, type ReactNode } from 'react'
import { AppstoreOutlined, DashboardOutlined, HomeOutlined, SettingOutlined, ToolOutlined } from '@ant-design/icons'

export type Permission = {
  resource: string
  action: string
}

export type NavigationMenuItem = {
  key: string
  label: string
  icon?: ReactNode
  requiredPermission?: Permission
  anyPermissions?: Permission[]
  platformOnly?: boolean
  children?: NavigationMenuItem[]
}

export type NavigationAccess = {
  isAdmin: boolean
  hasPermission: (resource: string, action: string) => boolean
}

const menuCatalog: NavigationMenuItem[] = [
  { label: '首页', key: '/home/dashboard', icon: createElement(HomeOutlined) },
  {
    label: '设备中心',
    key: '/gateway',
    icon: createElement(AppstoreOutlined),
    children: [
      { label: '产品管理', key: '/gateway/product/manage', requiredPermission: { resource: 'product', action: 'read' } },
      { label: '设备分组', key: '/gateway/device/group', requiredPermission: { resource: 'device', action: 'read' } },
      { label: '设备管理', key: '/gateway/device/manage', requiredPermission: { resource: 'device', action: 'read' } },
      { label: '批量配置', key: '/gateway/gnss/control', requiredPermission: { resource: 'device', action: 'execute' } },
      { label: 'OTA升级', key: '/gateway/ota', requiredPermission: { resource: 'ota', action: 'read' } },
    ],
  },
  {
    label: '可视化看板',
    key: '/dashboard',
    icon: createElement(DashboardOutlined),
    requiredPermission: { resource: 'dashboard', action: 'read' },
    children: [
      { label: '数据大屏', key: '/dashboard/screens' },
    ],
  },
  {
    label: '运维监控',
    key: '/monitor',
    icon: createElement(ToolOutlined),
    children: [
      { label: '系统监控', key: '/monitor/systemMonitor', requiredPermission: { resource: 'log', action: 'read' } },
      { label: '告警中心', key: '/monitor/alarmCenter/list', requiredPermission: { resource: 'alert', action: 'read' } },
    ],
  },
  {
    label: '系统管理',
    key: '/system',
    icon: createElement(SettingOutlined),
    children: [
      { label: '用户管理', key: '/system/user', platformOnly: true },
      { label: '账号管理', key: '/system/user-account', requiredPermission: { resource: 'user', action: 'read' } },
      { label: '权限与组织', key: '/system/authorization', anyPermissions: ['role', 'org', 'user'].map(resource => ({ resource, action: 'read' })) },
    ],
  },
]

function canUseMenu (item: NavigationMenuItem, access: NavigationAccess): boolean {
  if (item.platformOnly && !access.hasPermission('*', '*')) return false
  if (item.anyPermissions) return item.anyPermissions.some(({ resource, action }) => access.hasPermission(resource, action))
  if (!item.requiredPermission) return true
  const { resource, action } = item.requiredPermission
  return access.hasPermission(resource, action)
}

function cloneAllowedMenu (items: NavigationMenuItem[], access: NavigationAccess): NavigationMenuItem[] {
  return items.reduce<NavigationMenuItem[]>((result, item) => {
    if (!canUseMenu(item, access)) return result

    const children = item.children ? cloneAllowedMenu(item.children, access) : undefined
    if (item.children && (!children || children.length === 0)) return result

    result.push({
      key: item.key,
      label: item.label,
      icon: item.icon,
      requiredPermission: item.requiredPermission,
      anyPermissions: item.anyPermissions,
      platformOnly: item.platformOnly,
      ...(children ? { children } : {}),
    })
    return result
  }, [])
}

export function buildNavigationMenu (access: NavigationAccess): NavigationMenuItem[] {
  return cloneAllowedMenu(menuCatalog, access)
}

export function buildBreadcrumbNameMap (items: NavigationMenuItem[]): Record<string, string> {
  const names: Record<string, string> = {}
  const visit = (nodes: NavigationMenuItem[]) => {
    nodes.forEach((item) => {
      names[item.key] = item.label
      if (item.children) visit(item.children)
    })
  }
  visit(items)
  return names
}
